import type { V4HSiteId } from "@/data/common/ids";
import type { UpdateSectionsCategoriesRequest } from "@/domains/v4b/site-section/site-section.types";
import type { Ref } from "vue";

import { useMutation, useQueryClient } from "@tanstack/vue-query";

import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { SiteProgrammingRangeQueryKeys } from "@/data/api/v4b/site-programming-range/site-programming-range-query-keys";
import { SiteSectionMutationKeys } from "@/data/api/v4b/site-section/site-section-mutation-keys";

export function useUpdateSectionsCategoriesMutation(siteId: Ref<V4HSiteId | undefined>) {
  const v4b = useServices().v4b.clientSdk;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: siteId.value ? SiteSectionMutationKeys.updateSectionsCategories(siteId.value) : undefined,
    mutationFn: async (command: UpdateSectionsCategoriesRequest): Promise<V4HSiteId> => {
      const site = siteId.value;
      if (!site) {
        throw new Error("Site ID is required");
      }

      await v4b.siteSectionCategory.updateSectionsCategories(site, command);
      return site;
    },
    onSettled: async (siteId) => {
      if (siteId) {
        await queryClient.invalidateQueries({
          queryKey: SiteQueryKeys.sections(siteId),
        });

        await queryClient.removeQueries({
          queryKey: SiteProgrammingRangeQueryKeys.findPlanning(siteId),
        });
      }
    },
    retry: 0,
  });
}
